import './styles.css';
import { parse as parseWithSourceMap } from 'json-source-map';
import { JsonEditor } from './ui/json-editor.js';
import { diffJson, filterDifferences } from './lib/diff.js';
import {
  escapeJsonText,
  formatJson,
  getJsonChildRangesAtPosition,
  parseJson,
  stringifyDisplay,
  unescapeJsonText,
} from './lib/json-utils.js';
import { getAtPath, parseDotPath, toJsonPointer } from './lib/path-utils.js';
import { findKeyPaths } from './lib/key-search.js';
import {
  buildRequestBody,
  buildRequestHeaders,
  buildRequestUrl,
  formatResponseBody,
} from './lib/http-utils.js';
import { buildCurlCommand, CURL_OPTION_DEFINITIONS } from './lib/curl-utils.js';
import { HandleStore } from './services/handle-store.js';
import { JsonLibrary } from './services/library.js';

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => [...document.querySelectorAll(selector)];

const state = {
  dirty: false,
  replacingEditor: false,
  currentRecord: null,
  records: [],
  extractedText: '',
  keyMatches: [],
  activeKeyMatch: -1,
  selectedExtractSegments: null,
  selectedExtractPath: '',
  httpController: null,
  httpResponseText: '',
  curlCommand: '',
};

const library = new JsonLibrary(new HandleStore());
const mainEditor = new JsonEditor($('#mainEditor'), {
  value: '{\n  "hello": "world"\n}',
  onChange: () => {
    if (!state.replacingEditor) {
      setDirty(true);
      resetKeySearch();
    }
  },
});
const leftEditor = new JsonEditor($('#leftEditor'), { value: '{\n  "id": 1,\n  "name": "Alice"\n}' });
const rightEditor = new JsonEditor($('#rightEditor'), { value: '{\n  "id": 1,\n  "name": "Bob"\n}' });

setupTabs();
setupEditorActions();
setupLibraryActions();
setupDiffActions();
setupHttpActions();
setupCurlActions();
checkEnvironment();
restoreLibrary();

window.addEventListener('beforeunload', (event) => {
  if (!state.dirty) return;
  event.preventDefault();
  event.returnValue = '';
});

function setupTabs() {
  $$('.tab').forEach((button) => {
    button.addEventListener('click', () => activateTab(button.dataset.tab));
  });
}

function activateTab(panelId) {
  $$('.tab').forEach((tab) => tab.classList.toggle('active', tab.dataset.tab === panelId));
  $$('.panel').forEach((panel) => panel.classList.toggle('active', panel.id === panelId));
  if (panelId === 'libraryPanel' && library.connected) refreshLibrary();
}

function setupEditorActions() {
  $('#formatButton').addEventListener('click', () => transformEditor(formatJson, 'JSON 已格式化。'));
  $('#minifyButton').addEventListener('click', () => {
    const result = getJsonChildRangesAtPosition(mainEditor.getValue(), mainEditor.getCursorPosition());
    if (!result.ok) {
      showParseError(result.error, mainEditor);
      return;
    }
    mainEditor.collapseRanges(result.ranges);
    showEditorMessage('光标所在层级及同级节点的子节点已折叠。', 'success');
  });
  $('#expandButton').addEventListener('click', () => {
    const result = getJsonChildRangesAtPosition(mainEditor.getValue(), mainEditor.getCursorPosition());
    if (!result.ok) {
      showParseError(result.error, mainEditor);
      return;
    }
    const expanded = mainEditor.expandRanges(result.ranges);
    showEditorMessage(expanded ? '当前层级已展开一级。' : '当前层级没有可展开的折叠内容。', 'success');
  });
  $('#escapeButton').addEventListener('click', () => {
    replaceEditor(escapeJsonText(mainEditor.getValue()), true);
    showEditorMessage('文本已转义为 JSON 字符串。', 'success');
  });
  $('#unescapeButton').addEventListener('click', () => transformEditor(unescapeJsonText, 'JSON 字符串已反转义。'));
  $('#copyButton').addEventListener('click', async () => {
    await copyText(mainEditor.getValue());
    toast('已复制当前内容。');
  });
  $('#clearButton').addEventListener('click', clearDocument);
  $('#openSaveDialogButton').addEventListener('click', openSaveDialog);
  $('#saveButton').addEventListener('click', saveCurrentRecord);
  $('#cancelSaveButton').addEventListener('click', closeSaveDialog);
  $('#closeSaveDialogButton').addEventListener('click', closeSaveDialog);
  $('#saveDialog').addEventListener('click', (event) => {
    if (event.target === $('#saveDialog')) closeSaveDialog();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !$('#saveDialog').hidden) closeSaveDialog();
  });
  $('#extractButton').addEventListener('click', extractField);
  $('#extractPath').addEventListener('input', searchKeys);
  $('#extractPath').addEventListener('focus', () => {
    if (state.keyMatches.length) renderKeyResults();
  });
  $('#extractPath').addEventListener('keydown', (event) => {
    handleKeySearchKeyboard(event);
  });
  document.addEventListener('click', (event) => {
    if (!$('#keySearch').contains(event.target)) closeKeyResults();
  });
  $('#copyExtractButton').addEventListener('click', async () => {
    await copyText(state.extractedText);
    toast('已复制提取结果。');
  });
}

function transformEditor(transform, successMessage) {
  const result = transform(mainEditor.getValue());
  if (!result.ok) {
    showParseError(result.error, mainEditor);
    return;
  }
  replaceEditor(result.value, true);
  showEditorMessage(successMessage, 'success');
}

function replaceEditor(value, dirty) {
  state.replacingEditor = true;
  mainEditor.setValue(value);
  state.replacingEditor = false;
  setDirty(dirty);
  resetKeySearch();
}

function setDirty(dirty) {
  state.dirty = dirty;
  $('#dirtyState').textContent = dirty ? '● 未保存' : '';
}

function clearDocument() {
  if (mainEditor.getValue().trim() && !confirm('确定清空当前内容吗？未保存的修改将丢失。')) return;
  state.currentRecord = null;
  $('#recordTitle').value = '';
  $('#recordNote').value = '';
  $('#documentName').textContent = '未命名 JSON';
  replaceEditor('', false);
  showEditorMessage('编辑器内容已清空。');
  mainEditor.focus();
}

function openSaveDialog() {
  const dialog = $('#saveDialog');
  dialog.hidden = false;
  document.body.classList.add('modal-open');
  requestAnimationFrame(() => {
    $('#recordTitle').focus();
    $('#recordTitle').select();
  });
}

function closeSaveDialog() {
  $('#saveDialog').hidden = true;
  document.body.classList.remove('modal-open');
  $('#openSaveDialogButton').focus();
}

async function saveCurrentRecord() {
  if (!library.connected) {
    closeSaveDialog();
    activateTab('libraryPanel');
    toast('请先选择资料库目录。', true);
    return;
  }
  const parsed = parseJson(mainEditor.getValue());
  if (!parsed.ok) {
    closeSaveDialog();
    showParseError(parsed.error, mainEditor);
    return;
  }
  try {
    const saved = await library.save({
      current: state.currentRecord,
      title: $('#recordTitle').value,
      note: $('#recordNote').value,
      data: parsed.value,
    });
    state.currentRecord = saved;
    $('#documentName').textContent = saved.record.title;
    $('#recordTitle').value = saved.record.title;
    setDirty(false);
    closeSaveDialog();
    activateTab('libraryPanel');
    toast('JSON 已写入本地资料库。');
  } catch (error) {
    toast(error.message || '保存失败。', true);
  }
}

function extractField() {
  if (!state.selectedExtractSegments) {
    setExtractError('请先搜索 key，并从匹配列表选择一个路径。');
    return;
  }
  const parsed = parseJson(mainEditor.getValue());
  if (!parsed.ok) {
    showParseError(parsed.error, mainEditor);
    setExtractError('请先修复 JSON 语法错误。');
    return;
  }
  const result = getAtPath(parsed.value, state.selectedExtractSegments);
  if (!result.found) {
    setExtractError('所选路径已不存在，请重新搜索并选择。');
    resetKeySelection();
    return;
  }

  const type = Array.isArray(result.value) ? 'array' : result.value === null ? 'null' : typeof result.value;
  state.extractedText = stringifyDisplay(result.value);
  const output = $('#extractResult');
  output.classList.remove('empty');
  output.textContent = `类型：${type}\n\n${state.extractedText}`;
  $('#copyExtractButton').disabled = false;

  try {
    const sourceMap = parseWithSourceMap(mainEditor.getValue()).pointers;
    const pointer = toJsonPointer(result.segments);
    const location = sourceMap[pointer]?.value;
    const end = sourceMap[pointer]?.valueEnd;
    if (location) mainEditor.selectRange(location.pos, end?.pos ?? location.pos);
  } catch {
    // JSON.parse already validated the content; source mapping is an enhancement only.
  }
}

function searchKeys() {
  resetKeySelection();
  resetExtractOutput('请选择一个匹配路径，再点击确认提取。');
  const query = $('#extractPath').value;
  if (!query.trim()) {
    closeKeyResults();
    return;
  }

  const parsed = parseJson(mainEditor.getValue());
  if (!parsed.ok) {
    state.keyMatches = [];
    renderKeyResults('当前 JSON 存在语法错误，无法搜索 key。');
    return;
  }

  state.keyMatches = findKeyPaths(parsed.value, query);
  state.activeKeyMatch = state.keyMatches.length ? 0 : -1;
  renderKeyResults(state.keyMatches.length ? '' : '没有找到匹配的 key。');
}

function renderKeyResults(emptyMessage = '') {
  const results = $('#keySearchResults');
  results.replaceChildren();
  if (emptyMessage) {
    results.append(createElement('div', 'key-search-empty', emptyMessage));
  } else {
    state.keyMatches.forEach((match, index) => {
      const option = createElement('button', `key-search-option${index === state.activeKeyMatch ? ' active' : ''}`);
      option.type = 'button';
      option.role = 'option';
      option.setAttribute('aria-selected', String(index === state.activeKeyMatch));
      option.append(createElement('strong', '', match.key), createElement('span', '', match.path));
      option.addEventListener('click', () => selectKeyMatch(index));
      results.append(option);
    });
  }
  results.hidden = false;
  $('#extractPath').setAttribute('aria-expanded', 'true');
}

function handleKeySearchKeyboard(event) {
  if ($('#keySearchResults').hidden || !state.keyMatches.length) {
    if (event.key === 'Escape') closeKeyResults();
    return;
  }
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    state.activeKeyMatch = (state.activeKeyMatch + direction + state.keyMatches.length) % state.keyMatches.length;
    renderKeyResults();
    $('#keySearchResults').children[state.activeKeyMatch]?.scrollIntoView({ block: 'nearest' });
  } else if (event.key === 'Enter') {
    event.preventDefault();
    selectKeyMatch(state.activeKeyMatch);
  } else if (event.key === 'Escape') {
    closeKeyResults();
  }
}

function selectKeyMatch(index) {
  const match = state.keyMatches[index];
  if (!match) return;
  state.selectedExtractSegments = match.segments;
  state.selectedExtractPath = match.path;
  $('#selectedPath').textContent = `已选择：${match.path}`;
  $('#extractButton').disabled = false;
  resetExtractOutput('路径已选择，请点击“确认定位并提取”。');
  closeKeyResults();
}

function resetKeySearch() {
  $('#extractPath').value = '';
  resetKeySelection();
  resetExtractOutput('尚未提取字段');
  closeKeyResults();
}

function resetKeySelection() {
  state.keyMatches = [];
  state.activeKeyMatch = -1;
  state.selectedExtractSegments = null;
  state.selectedExtractPath = '';
  $('#selectedPath').textContent = '请先从匹配列表选择路径';
  $('#extractButton').disabled = true;
}

function resetExtractOutput(message) {
  state.extractedText = '';
  $('#extractResult').classList.add('empty');
  $('#extractResult').textContent = message;
  $('#copyExtractButton').disabled = true;
}

function closeKeyResults() {
  $('#keySearchResults').hidden = true;
  $('#extractPath').setAttribute('aria-expanded', 'false');
}

function setExtractError(message) {
  resetExtractOutput(message);
}

function setupLibraryActions() {
  $('#chooseDirectoryButton').addEventListener('click', async () => {
    try {
      await library.chooseDirectory();
      await onDirectoryConnected();
    } catch (error) {
      if (error.name !== 'AbortError') toast(error.message || '无法打开目录。', true);
    }
  });
  $('#restoreDirectoryButton').addEventListener('click', async () => {
    try {
      if (await library.requestPermission()) await onDirectoryConnected();
      else toast('未获得目录读写权限。', true);
    } catch (error) {
      toast(error.message || '恢复目录访问失败。', true);
    }
  });
  $('#refreshLibraryButton').addEventListener('click', refreshLibrary);
  $('#librarySearch').addEventListener('input', renderRecordList);
}

async function restoreLibrary() {
  try {
    const handle = await library.findStoredDirectory();
    if (!handle) return;
    const permission = await library.permissionState();
    $('#libraryStatus').textContent = `上次目录：${handle.name}`;
    if (permission === 'granted') await onDirectoryConnected();
    else $('#restoreDirectoryButton').hidden = false;
  } catch (error) {
    $('#libraryStatus').textContent = `无法恢复目录：${error.message}`;
  }
}

async function onDirectoryConnected() {
  $('#libraryStatus').textContent = `当前目录：${library.directoryHandle.name}`;
  $('#restoreDirectoryButton').hidden = true;
  $('#refreshLibraryButton').disabled = false;
  $('#saveHint').textContent = `将保存到：${library.directoryHandle.name}`;
  await refreshLibrary();
}

async function refreshLibrary() {
  if (!library.connected) return;
  $('#libraryStatus').textContent = `正在扫描：${library.directoryHandle.name}…`;
  try {
    const result = await library.scan();
    state.records = result.records;
    $('#libraryStatus').textContent = `当前目录：${library.directoryHandle.name}`;
    renderWarnings(result.warnings);
    renderRecordList();
  } catch (error) {
    $('#libraryStatus').textContent = '目录访问失败';
    $('#restoreDirectoryButton').hidden = false;
    toast(error.message || '无法扫描目录。', true);
  }
}

function renderWarnings(warnings) {
  const box = $('#libraryWarnings');
  box.hidden = warnings.length === 0;
  box.textContent = warnings.length
    ? `${warnings.length} 个记录文件未载入：${warnings.map((item) => `${item.fileName}（${item.message}）`).join('；')}`
    : '';
}

function renderRecordList() {
  const query = $('#librarySearch').value.trim().toLocaleLowerCase();
  const records = state.records.filter(({ record }) => record.title.toLocaleLowerCase().includes(query));
  $('#recordCount').textContent = `${records.length} 条记录`;
  const list = $('#libraryList');
  list.replaceChildren();
  if (!records.length) {
    list.append(createElement('div', 'empty-state', query ? '没有匹配的标题。' : '目录中还没有 JSON 工具记录。'));
    return;
  }
  for (const item of records) {
    const card = createElement('article', 'record-card');
    card.tabIndex = 0;
    const heading = createElement('h3', '', item.record.title);
    const note = createElement('p', '', item.record.note || '无备注');
    const meta = createElement('div', 'record-meta');
    meta.append(
      createElement('span', '', formatDate(item.record.updatedAt)),
      createElement('span', '', typeLabel(item.record.data)),
    );
    card.append(heading, note, meta);
    const load = () => loadRecord(item);
    card.addEventListener('click', load);
    card.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') load();
    });
    list.append(card);
  }
}

function loadRecord(item) {
  if (state.dirty && !confirm('当前编辑内容尚未保存，确定要加载其他记录吗？')) return;
  state.currentRecord = item;
  $('#recordTitle').value = item.record.title;
  $('#recordNote').value = item.record.note;
  $('#documentName').textContent = item.record.title;
  replaceEditor(JSON.stringify(item.record.data, null, 2), false);
  showEditorMessage(`已加载：${item.fileName}`, 'success');
  activateTab('editorPanel');
}

function setupDiffActions() {
  $('#compareButton').addEventListener('click', compareEditors);
  $('#diffPath').addEventListener('keydown', (event) => {
    if (event.key === 'Enter') compareEditors();
  });
}

function compareEditors() {
  const left = parseJson(leftEditor.getValue());
  const right = parseJson(rightEditor.getValue());
  if (!left.ok || !right.ok) {
    const side = !left.ok ? '左侧' : '右侧';
    const error = !left.ok ? left.error : right.error;
    $('#diffMessage').textContent = `${side} JSON 无效：${error.message}`;
    $('#diffMessage').style.color = 'var(--danger)';
    showParseError(error, !left.ok ? leftEditor : rightEditor);
    return;
  }

  const filter = $('#diffPath').value;
  try {
    parseDotPath(filter);
  } catch (error) {
    $('#diffMessage').textContent = error.message;
    $('#diffMessage').style.color = 'var(--danger)';
    return;
  }
  const all = diffJson(left.value, right.value);
  const differences = filterDifferences(all, filter);
  renderDifferences(differences);
  $('#diffMessage').style.color = '';
  $('#diffMessage').textContent = filter.trim() ? `已按路径“${filter.trim()}”筛选` : '已完成完整结构对比';
}

function renderDifferences(differences) {
  const body = $('#diffResults');
  body.replaceChildren();
  $('#diffCount').textContent = `${differences.length} 处差异`;
  if (!differences.length) {
    const row = document.createElement('tr');
    const cell = createElement('td', 'empty-cell', '没有发现差异');
    cell.colSpan = 4;
    row.append(cell);
    body.append(row);
    return;
  }

  const labels = { added: '新增', removed: '删除', changed: '修改' };
  for (const difference of differences) {
    const row = document.createElement('tr');
    const typeCell = document.createElement('td');
    typeCell.append(createElement('span', `diff-type diff-${difference.type}`, labels[difference.type]));
    row.append(
      createElement('td', 'path-cell', difference.path || '(根节点)'),
      typeCell,
      createElement('td', 'value-cell', stringifyDisplay(difference.leftValue)),
      createElement('td', 'value-cell', stringifyDisplay(difference.rightValue)),
    );
    body.append(row);
  }
}

function setupHttpActions() {
  $('#addQueryParamButton').addEventListener('click', () => addKeyValueRow('query'));
  $('#addHeaderButton').addEventListener('click', () => addKeyValueRow('header'));
  $('#httpMethod').addEventListener('change', updateHttpBodyState);
  $('#httpBodyType').addEventListener('change', updateHttpBodyState);
  $('#httpAuthType').addEventListener('change', updateHttpAuthFields);
  $('#httpUrl').addEventListener('keydown', (event) => {
    if (event.key === 'Enter') sendHttpRequest();
  });
  $('#sendHttpButton').addEventListener('click', () => {
    if (state.httpController) state.httpController.abort();
    else sendHttpRequest();
  });
  $('#copyHttpResponseButton').addEventListener('click', async () => {
    await copyText(state.httpResponseText);
    toast('已复制响应体。');
  });

  addKeyValueRow('query', { focus: false });
  addKeyValueRow('header', { key: 'Accept', value: 'application/json' });
  updateHttpAuthFields();
  updateHttpBodyState();
}

function addKeyValueRow(kind, initial = {}) {
  const container = kind === 'query' ? $('#queryParamRows') : $('#headerRows');
  container.querySelector('.key-value-empty')?.remove();
  const row = createElement('div', 'key-value-row');
  row.dataset.kind = kind;

  const enabled = document.createElement('input');
  enabled.type = 'checkbox';
  enabled.checked = initial.enabled !== false;
  enabled.setAttribute('aria-label', '启用此项');

  const key = document.createElement('input');
  key.className = 'parameter-key';
  key.value = initial.key || '';
  key.placeholder = kind === 'query' ? '参数名' : '请求头名称';
  key.setAttribute('list', kind === 'query' ? 'queryKeySuggestions' : 'headerKeySuggestions');
  key.setAttribute('aria-label', key.placeholder);

  const value = document.createElement('input');
  value.className = 'parameter-value';
  value.value = initial.value || '';
  value.placeholder = '值';
  value.setAttribute('aria-label', `${key.placeholder}的值`);
  if (kind === 'header') value.setAttribute('list', 'headerValueSuggestions');

  const remove = createElement('button', 'remove-row-button', '×');
  remove.type = 'button';
  remove.title = '删除此项';
  remove.setAttribute('aria-label', '删除此项');
  remove.addEventListener('click', () => {
    row.remove();
    updateParameterCount(kind);
    if (!container.children.length) {
      container.append(createElement('div', 'key-value-empty', kind === 'query' ? '暂无查询参数' : '暂无自定义请求头'));
    }
  });
  [enabled, key, value].forEach((element) => element.addEventListener('input', () => updateParameterCount(kind)));
  row.append(enabled, key, value, remove);
  container.append(row);
  updateParameterCount(kind);
  if (!initial.key && initial.focus !== false) key.focus();
}

function collectKeyValueRows(kind) {
  const container = kind === 'query' ? $('#queryParamRows') : $('#headerRows');
  return [...container.querySelectorAll('.key-value-row')].map((row) => ({
    enabled: row.querySelector('input[type="checkbox"]').checked,
    key: row.querySelector('.parameter-key').value,
    value: row.querySelector('.parameter-value').value,
  }));
}

function updateParameterCount(kind) {
  const count = collectKeyValueRows(kind).filter((item) => item.enabled && item.key.trim()).length;
  $(kind === 'query' ? '#queryParamCount' : '#headerParamCount').textContent = `${count} 个已启用`;
}

function updateHttpAuthFields() {
  const type = $('#httpAuthType').value;
  $('#bearerTokenField').hidden = type !== 'bearer';
  $('#basicUsernameField').hidden = type !== 'basic';
  $('#basicPasswordField').hidden = type !== 'basic';
}

function updateHttpBodyState() {
  const methodAllowsBody = !['GET', 'HEAD'].includes($('#httpMethod').value);
  const type = $('#httpBodyType').value;
  const enabled = methodAllowsBody && type !== 'none';
  const input = $('#httpRequestBody');
  input.disabled = !enabled;
  if (!methodAllowsBody) {
    $('#httpBodyHelp').textContent = `${$('#httpMethod').value} 请求不会发送请求体。`;
  } else if (type === 'json') {
    input.placeholder = '{\n  "name": "Alice"\n}';
    $('#httpBodyHelp').textContent = '发送前会校验 JSON，并自动设置 application/json。';
  } else if (type === 'form') {
    input.placeholder = 'name=Alice\nrole=admin';
    $('#httpBodyHelp').textContent = '每行填写一个 key=value，发送时会自动编码。';
  } else if (type === 'text') {
    input.placeholder = '输入纯文本请求体';
    $('#httpBodyHelp').textContent = '默认使用 text/plain，可在请求头中覆盖。';
  } else {
    $('#httpBodyHelp').textContent = '当前请求不发送请求体。';
  }
}

function getHttpAuth() {
  return {
    type: $('#httpAuthType').value,
    token: $('#httpBearerToken').value,
    username: $('#httpBasicUsername').value,
    password: $('#httpBasicPassword').value,
  };
}

async function sendHttpRequest() {
  let url;
  let headers;
  let body;
  try {
    url = buildRequestUrl($('#httpUrl').value, collectKeyValueRows('query'));
    headers = buildRequestHeaders(collectKeyValueRows('header'), getHttpAuth());
    if (!['GET', 'HEAD'].includes($('#httpMethod').value)) {
      body = buildRequestBody($('#httpBodyType').value, $('#httpRequestBody').value, headers);
    }
  } catch (error) {
    showHttpError(error.message);
    return;
  }

  const controller = new AbortController();
  state.httpController = controller;
  const timeout = Number($('#httpTimeout').value);
  let timedOut = false;
  const timeoutTimer = timeout ? setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeout) : null;
  const startedAt = performance.now();
  setHttpLoading(true);

  try {
    const response = await fetch(url, {
      method: $('#httpMethod').value,
      headers,
      body,
      credentials: $('#httpCredentials').value,
      signal: controller.signal,
    });
    const text = await response.text();
    const elapsed = Math.round(performance.now() - startedAt);
    renderHttpResponse(response, text, elapsed);
  } catch (error) {
    if (error.name === 'AbortError') {
      showHttpError(timedOut ? `请求已超时（${timeout / 1000} 秒）。` : '请求已取消。');
    } else {
      showHttpError(`请求失败：${error.message}。请检查网络、URL，以及目标服务是否允许浏览器跨域访问（CORS）。`);
    }
  } finally {
    if (timeoutTimer) clearTimeout(timeoutTimer);
    state.httpController = null;
    setHttpLoading(false);
  }
}

function setHttpLoading(loading) {
  const button = $('#sendHttpButton');
  button.textContent = loading ? '取消请求' : '发送请求';
  button.classList.toggle('primary', !loading);
  if (loading) $('#httpResponseMeta').replaceChildren(createElement('span', '', '请求中…'));
}

function renderHttpResponse(response, text, elapsed) {
  state.httpResponseText = formatResponseBody(text, response.headers.get('content-type') || '');
  $('#httpResponseEmpty').hidden = true;
  $('#httpResponseContent').hidden = false;
  $('#httpResponseBody').textContent = state.httpResponseText || '（空响应体）';
  const headerLines = [...response.headers.entries()].map(([key, value]) => `${key}: ${value}`);
  $('#httpResponseHeaders').textContent = headerLines.join('\n') || '（浏览器未提供可读取的响应头）';
  $('#httpResponseHeaderCount').textContent = `· ${headerLines.length} 项`;
  $('#httpResponseUrl').textContent = `最终地址：${response.url}`;

  const meta = $('#httpResponseMeta');
  meta.replaceChildren();
  const status = createElement('span', `status-pill ${response.ok ? 'status-success' : 'status-error'}`, `${response.status} ${response.statusText}`.trim());
  meta.append(status, createElement('span', '', `${elapsed} ms · ${formatByteLength(text)}`));
}

function showHttpError(message) {
  state.httpResponseText = message;
  $('#httpResponseEmpty').hidden = true;
  $('#httpResponseContent').hidden = false;
  $('#httpResponseBody').textContent = message;
  $('#httpResponseHeaders').textContent = '（无响应头）';
  $('#httpResponseHeaderCount').textContent = '';
  $('#httpResponseUrl').textContent = '';
  const meta = $('#httpResponseMeta');
  meta.replaceChildren(createElement('span', 'status-pill status-error', '请求失败'));
}

function formatByteLength(text) {
  const bytes = new TextEncoder().encode(text).length;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function setupCurlActions() {
  $('#addCurlOptionButton').addEventListener('click', () => addCurlOptionRow());
  $('#generateCurlButton').addEventListener('click', () => generateCurlCommand());
  $('#copyCurlButton').addEventListener('click', async () => {
    await copyText(state.curlCommand);
    toast('cURL 命令已复制。');
  });
  $('#curlMethod').addEventListener('change', () => generateCurlCommand(true));
  $('#curlUrl').addEventListener('input', () => generateCurlCommand(true));
  $('#curlUrl').addEventListener('keydown', (event) => {
    if (event.key === 'Enter') generateCurlCommand();
  });
  addCurlOptionRow({ focus: false });
  generateCurlCommand(true);
}

function addCurlOptionRow(initial = {}) {
  const row = createElement('div', 'curl-option-row');
  const enabled = document.createElement('input');
  enabled.type = 'checkbox';
  enabled.checked = initial.enabled !== false;
  enabled.setAttribute('aria-label', '启用此参数');

  const type = document.createElement('select');
  type.className = 'curl-option-type';
  type.setAttribute('aria-label', 'cURL 参数');
  for (const definition of CURL_OPTION_DEFINITIONS) {
    const option = document.createElement('option');
    option.value = definition.value;
    option.textContent = definition.label;
    type.append(option);
  }
  type.value = initial.type || '';

  const value = document.createElement('input');
  value.className = 'curl-option-value';
  value.value = initial.value || '';
  value.setAttribute('aria-label', '参数值');
  value.spellcheck = false;

  const remove = createElement('button', 'remove-row-button', '×');
  remove.type = 'button';
  remove.title = '删除此参数';
  remove.setAttribute('aria-label', '删除此参数');
  remove.addEventListener('click', () => {
    row.remove();
    if (!$('#curlOptionRows').children.length) addCurlOptionRow({ focus: false });
    generateCurlCommand(true);
  });

  const updateValueState = () => {
    const definition = CURL_OPTION_DEFINITIONS.find((item) => item.value === type.value);
    value.placeholder = definition?.placeholder || '参数值';
    value.disabled = Boolean(definition && !definition.requiresValue);
    if (type.value === 'header') value.setAttribute('list', 'curlHeaderSuggestions');
    else value.removeAttribute('list');
    if (value.disabled) value.value = '';
  };
  type.addEventListener('change', () => {
    updateValueState();
    generateCurlCommand(true);
    if (!value.disabled && type.value) value.focus();
  });
  enabled.addEventListener('change', () => generateCurlCommand(true));
  value.addEventListener('input', () => generateCurlCommand(true));
  row.append(enabled, type, value, remove);
  $('#curlOptionRows').append(row);
  updateValueState();
  if (initial.focus !== false) type.focus();
}

function collectCurlOptions() {
  return [...$('#curlOptionRows').querySelectorAll('.curl-option-row')].map((row) => ({
    enabled: row.querySelector('input[type="checkbox"]').checked,
    type: row.querySelector('.curl-option-type').value,
    value: row.querySelector('.curl-option-value').value,
  }));
}

function generateCurlCommand(quiet = false) {
  if (quiet && !$('#curlUrl').value.trim()) {
    resetCurlOutput();
    return;
  }
  try {
    const command = buildCurlCommand({
      url: $('#curlUrl').value,
      method: $('#curlMethod').value,
      options: collectCurlOptions(),
    });
    state.curlCommand = command;
    const output = $('#curlOutput');
    output.textContent = command;
    output.classList.remove('empty');
    $('#copyCurlButton').disabled = false;
    $('#curlMessage').textContent = '命令已生成，可直接复制。';
    $('#curlMessage').classList.remove('error');
  } catch (error) {
    state.curlCommand = '';
    $('#curlOutput').textContent = quiet ? '请补全参数后重新生成。' : '暂时无法生成命令。';
    $('#curlOutput').classList.add('empty');
    $('#copyCurlButton').disabled = true;
    $('#curlMessage').textContent = error.message;
    $('#curlMessage').classList.add('error');
  }
}

function resetCurlOutput() {
  state.curlCommand = '';
  $('#curlOutput').textContent = '生成的 cURL 命令会显示在这里。';
  $('#curlOutput').classList.add('empty');
  $('#copyCurlButton').disabled = true;
  $('#curlMessage').textContent = '填写 URL 后即可生成。';
  $('#curlMessage').classList.remove('error');
}

function showParseError(error, editor) {
  const location = error.line ? `（第 ${error.line} 行，第 ${error.column} 列）` : '';
  showEditorMessage(`JSON 语法错误${location}：${error.message}`, 'error');
  if (Number.isInteger(error.position)) editor.selectRange(error.position);
}

function showEditorMessage(message, type = '') {
  const element = $('#editorMessage');
  element.textContent = message;
  element.className = `message-line ${type}`.trim();
}

async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.append(area);
  area.select();
  document.execCommand('copy');
  area.remove();
}

function checkEnvironment() {
  const supported = Boolean(globalThis.isSecureContext && globalThis.showDirectoryPicker);
  const badge = $('#environmentBadge');
  badge.textContent = supported ? '本地安全模式' : '文件能力受限';
  if (!supported) badge.title = '目录读写需要最新版 Chrome/Edge，并通过 HTTPS 或 localhost 访问。';
}

function toast(message, error = false) {
  const element = $('#toast');
  element.textContent = message;
  element.className = `toast visible${error ? ' error' : ''}`;
  clearTimeout(toast.timer);
  toast.timer = setTimeout(() => { element.className = 'toast'; }, 2600);
}

function createElement(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

function formatDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function typeLabel(value) {
  if (Array.isArray(value)) return `数组 · ${value.length} 项`;
  if (value !== null && typeof value === 'object') return `对象 · ${Object.keys(value).length} 项`;
  return value === null ? 'null' : typeof value;
}
